import React, { useState, useContext, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import { LoginContext } from "../../../../contexts/LoginContext";
import routes from "../../../../config/routes-config";
import { createProject } from "./api-handlers";
import validateProjectDetails from "./validation";
import { useNavigate } from "react-router-dom";

function ProjectDetails({
  projectType,
  academicStartYear,
  semester,
  division,
  groupNumber,
  projectYearOfDevelopment,
  projectDevelopmentDepartment,
  deadline,
  projectGuideId,
  projectContributorIds,
}) {
  this.projectType = projectType;
  this.academicYearAndSem = `${academicStartYear}-${(
    parseInt(academicStartYear) + 1
  ).toString()} ${semester}`;
  this.divisionGroupNumber = `${division} ${groupNumber}`.trim();
  this.projectYearOfDevelopment = projectYearOfDevelopment;
  this.projectDevelopmentDepartment = projectDevelopmentDepartment;
  this.deadline = deadline;
  this.projectGuideId = projectGuideId;
  this.projectContributorIds = projectContributorIds;
}

const CreateNewProject = ({ projectType }) => {
  const { userAuthStatus, user, departments } = useContext(LoginContext);

  const navigate = useNavigate();

  const [projectDetails, setProjectDetails] = useState(() => {
    return {
      projectType: "",
      academicStartYear: "",
      semester: "",
      division: "",
      groupNumber: "",
      projectYearOfDevelopment: "",
      projectDevelopmentDepartment: "",
      deadline: "",
      projectGuideId: "",
      projectContributorIds: [""],
    };
  });

  const [errors, setErrors] = useState({});

  const semesters = ["Sem-1", "Sem-2"];

  const years = ["FY", "SY", "TY", "B.Tech"];

  useEffect(() => {
    if (userAuthStatus && user !== null) {
      setProjectDetails((previousState) => {
        return {
          ...previousState,
          projectType: projectType,
          projectGuideId: user.userName,
        };
      });
    }
  }, [userAuthStatus, user, projectType]);

  const handleProjectDetails = (event) => {
    setProjectDetails((previousState) => {
      return {
        ...previousState,
        [event.target.name]: event.target.value,
      };
    });
  };

  const handleChangeSdpProjectContributor = (event) => {
    setProjectDetails((previousState) => {
      return {
        ...previousState,
        [event.target.name]: [event.target.value],
      };
    });
  };

  const handleChangeEdaiProjectContributor = (event, index) => {
    setProjectDetails((previousState) => {
      const newProjectContributorsList = [
        ...previousState.projectContributorIds,
      ];

      newProjectContributorsList[index] = event.target.value;

      return {
        ...previousState,
        [event.target.name]: newProjectContributorsList,
      };
    });
  };

  const handleAddEdaiProjectContributor = (event) => {
    setProjectDetails((previousState) => {
      return {
        ...previousState,
        [event.target.name]: [...previousState.projectContributorIds, ""],
      };
    });
  };

  const handleRemoveEdaiProjectContributor = (event, index) => {
    setProjectDetails((previousState) => {
      const newProjectContributorsList = [
        ...previousState.projectContributorIds,
      ];
      newProjectContributorsList.splice(index, 1);

      return {
        ...previousState,
        [event.target.name]: newProjectContributorsList,
      };
    });
  };

  const handleProjectDetailsSubmission = (event) => {
    event.preventDefault();

    const errors = validateProjectDetails(projectDetails);
    setErrors((previousState) => {
      return { ...previousState, ...errors };
    });

    if (Object.keys(errors).length === 0 && userAuthStatus && user !== null) {
      const finalProjectDetails = new ProjectDetails(projectDetails);

      createProject(
        routes.teacher.ongoingProjects.createProject,
        user.token,
        finalProjectDetails,
        toast,
        navigate
      );
    }
  };

  return (
    <div className="w-full bg-[#ECECEC] flex flex-col py-10 px-20">
      <h1 className="w-full text-3xl font-semibold text-[#1E1E4A] mb-10">
        Create Project : {projectType}
      </h1>

      <form
        onSubmit={handleProjectDetailsSubmission}
        className="flex flex-col align-center space-y-10"
      >
        <div className="rounded-2xl bg-white px-20 py-10 space-y-10">
          {/* Row - 1 */}
          <div className="flex flex-col space-y-4">
            <div className="flex flex-row flex-wrap items-center space-x-4">
              <label
                htmlFor="deadline"
                className="text-xl text-[#444444] font-bold"
              >
                Deadline:
              </label>
              <input
                id="deadline"
                type="date"
                name="deadline"
                value={projectDetails.deadline}
                onChange={handleProjectDetails}
                min={new Date().toISOString().split("T")[0]}
                className="min-w-fit h-full rounded-lg"
              />
            </div>
            {errors.deadline && (
              <p className="text-red-700">{errors.deadline}</p>
            )}
          </div>

          {/* Row - 2 */}
          <div className="flex md:flex-col lg:flex-row sm:flex-wrap lg:flex-wrap lg:justify-between">
            <div className="flex flex-col sm:w-full lg:w-1/5 h-full md:space-y-4">
              <select
                placeholder="Year"
                name="projectYearOfDevelopment"
                value={projectDetails.projectYearOfDevelopment}
                onChange={handleProjectDetails}
                className="rounded-lg px-4 py-4"
              >
                <option value="" disabled>
                  Select Year
                </option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {errors.projectYearOfDevelopment && (
                <p className="text-red-500">
                  {errors.projectYearOfDevelopment}
                </p>
              )}
            </div>

            <div className="flex flex-col sm:w-full lg:w-1/5 sm:space-y-4 h-full">
              <select
                placeholder="Semester"
                name="semester"
                value={projectDetails.semester}
                onChange={handleProjectDetails}
                className="rounded-lg px-4 py-4"
              >
                <option value="" onChange={handleProjectDetails}>
                  Select Semester
                </option>
                {semesters.map((semester, index) => (
                  <option key={index} value={semester}>
                    {semester}
                  </option>
                ))}
              </select>
              {errors.semester && (
                <p className="text-red-500">{errors.semester}</p>
              )}
            </div>

            <div className="flex flex-col sm:w-full lg:w-1/5 sm:space-y-4 h-full">
              <input
                placeholder="Division"
                type="text"
                name="division"
                value={projectDetails.division}
                onChange={handleProjectDetails}
                className="rounded-lg px-4 py-4"
              />
              {errors.division && (
                <p className="text-red-500">{errors.division}</p>
              )}
            </div>

            <div className="flex flex-col sm:w-full lg:w-1/5 sm:space-y-4 h-full">
              <select
                placeholder="Departments"
                name="projectDevelopmentDepartment"
                value={projectDetails.projectDevelopmentDepartment}
                onChange={handleProjectDetails}
                className="rounded-lg px-4 py-4"
              >
                <option value="">Select Department</option>
                <option value="CS">Computer Engineering</option>
                <option value="IT">Information Technology</option>
                {departments.map((department, index) => (
                  <option key={index} value={department.departmentAbbr}>
                    {department.departmentName}
                  </option>
                ))}
              </select>
              {errors.projectDevelopmentDepartment && (
                <p className="text-red-500">
                  {errors.projectDevelopmentDepartment}
                </p>
              )}
            </div>
          </div>

          {/* Row - 3 */}
          <div className="flex md:flex-col lg:flex-row flex-wrap md:justify-center md:items-start lg:justify-start lg:items-center lg:space-x-20">
            <div className="flex flex-col space-y-4">
              <div className="flex flex-row items-center">
                <label className="mr-4">Academic Year: </label>
                <input
                  type="number"
                  placeholder="Academic Start Year"
                  name="academicStartYear"
                  value={projectDetails.academicStartYear}
                  onChange={handleProjectDetails}
                  className="rounded-lg px-4 py-4"
                />
                <p className="ml-4 mr-4">-</p>
                <input
                  type="number"
                  placeholder="Academic End Year"
                  name="academicEndYear"
                  value={
                    projectDetails.academicStartYear === ""
                      ? ""
                      : (
                          parseInt(projectDetails.academicStartYear) + 1
                        ).toString()
                  }
                  className="bg-gray-300 rounded-lg px-4 py-4"
                  disabled
                />
              </div>
              {errors.academicStartYear && (
                <p className="text-red-500">{errors.academicStartYear}</p>
              )}
            </div>

            {projectType === "EDAI" ? (
              <div className="flex flex-col space-y-4">
                <div className="flex flex-row items-center">
                  <label htmlFor="groupNo" className="mr-4">
                    Group No:{" "}
                  </label>
                  <input
                    id="groupNo"
                    type="number"
                    placeholder="Group No"
                    name="groupNumber"
                    value={projectDetails.groupNumber}
                    onChange={handleProjectDetails}
                    className="rounded-lg px-4 py-4"
                  />
                </div>
                {errors.groupNumber && (
                  <p className="text-red-500">{errors.groupNumber}</p>
                )}
              </div>
            ) : (
              <div className="flex flex-col space-y-4 md:mt-8">
                <div className="flex flex-row items-center">
                  <label htmlFor="student" className="mr-4">
                    Student GR No:{" "}
                  </label>
                  <input
                    id="studentGrNo"
                    type="number"
                    placeholder="Student GR No"
                    name="projectContributorIds"
                    value={[projectDetails.projectContributorIds]}
                    onChange={handleChangeSdpProjectContributor}
                    className="rounded-lg px-4 py-4"
                  />
                </div>
                {errors.studentGrNo && (
                  <p className="text-red-500">{errors.studentGrNo}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {projectType === "EDAI" && (
          <div className="h-[calc(40vh)] py-10 bg-white overflow-y-auto rounded-2xl">
            {projectDetails.projectContributorIds.map((student, index) => (
              <div
                key={index}
                className="flex flex-col justify-center items-center mb-5"
              >
                <div>
                  <label
                    htmlFor="student"
                    className="text-xl text-[#4F4E4E] mr-4"
                  >
                    {`Student-${index + 1} GR No`}
                  </label>
                  <input
                    id="student"
                    type="number"
                    name="projectContributorIds"
                    value={student}
                    placeholder={`Student-${index + 1} GR No`}
                    className="py-4 pl-4 rounded-lg mr-4"
                    onChange={(event) =>
                      handleChangeEdaiProjectContributor(event, index)
                    }
                  />
                  <button
                    type="button"
                    name="projectContributorIds"
                    onClick={(event) =>
                      handleRemoveEdaiProjectContributor(event, index)
                    }
                    className={`${
                      projectDetails.projectContributorIds.length === 1
                        ? "invisible"
                        : "visible"
                    } inline-block rounded-lg px-6 py-2.5 bg-red-600 text-white font-medium text-sm leading-tight uppercase shadow-md hover:bg-red-700 hover:shadow-lg focus:bg-red-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-red-800 active:shadow-lg transition duration-150 ease-in-out`}
                  >
                    Remove Student
                  </button>
                  {projectDetails.projectContributorIds.length - 1 ===
                    index && (
                    <div className="flex flex-row justify-center items-center mt-10">
                      <button
                        type="button"
                        name="projectContributorIds"
                        onClick={handleAddEdaiProjectContributor}
                        className="inline-block rounded-lg px-6 py-2.5 bg-green-500 text-white font-medium text-sm leading-tight uppercase shadow-md hover:bg-green-600 hover:shadow-lg focus:bg-green-600 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-green-700 active:shadow-lg transition duration-150 ease-in-out"
                      >
                        Add Student
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {errors.noOfContributors && (
              <p className="text-red-700 text-center mt-5">
                {errors.noOfContributors}
              </p>
            )}
          </div>
        )}
        <div className="flex flex-row justify-center items-center">
          <input
            type="submit"
            value="Create Project"
            className="cursor-pointer inline-block rounded-lg px-6 py-2.5 bg-[#6464da] text-[#FFFDFD] font-medium text-sm leading-tight uppercase shadow-md hover:bg-[#5656e0] hover:shadow-lg focus:bg-[#26267b] text-[#FFFDFD]focus:shadow-lg focus:outline-none focus:ring-0 active:bg-[#2121ca] active:shadow-lg transition duration-150 ease-in-out"
          />
        </div>
      </form>

      <ToastContainer />
    </div>
  );
};

export default CreateNewProject;
